import { Flex, Image, Pagination, Spin, Typography } from 'antd';
import type { FC } from 'react';
import { useMemo, useState } from 'react';
import type { DriveFolderType, SerializedMeterAddon, SerializedMeterModel } from '@enmon/commons/lib/tools';
import { useMeterModelFiles } from 'api/hooks/meter-models/useMeterModelFiles';
import { useMeterModuleFiles } from 'api/hooks/meter-modules/useMeterModuleFiles';
import { useServices } from 'containers/services';
import { Icon } from 'components/icon';
import styled, { useTheme } from 'styled-components';
import type { PopulatedCombinedMeter } from '@/lib/services';
import { useFiles } from '@/api/hooks/files/useFiles';
import { Collection, FileType, MeterFileValue } from '@enmon/commons/lib/enmon';
import { useDocPreviewModal } from './DocPreviewModal';
import { DRIVE_FOLDER_TYPE_TRANSLATED } from '../constants';
import { StyledCarousel } from '../StyledCarousel';
import { FileCard } from './FileCard';
import { getImageSrcFromBuffer } from './helpers';
import { MeterModelFilesCardType } from '@/routes/reseller-data/routes/model-library/routes/models/routes/detail/constants';

const { Text } = Typography;

const PHOTO_HEIGHT = 300; // every slide the same height: the card no longer grows with the number of photos
const DOCS_PER_PAGE = 4; // document tiles per page: a 2 × 2 grid

const StyledDocumentGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  justify-items: center;
  gap: 16px;
  width: 100%;
`;

export interface ModelLibraryFilesGalleryProps {
  model?: SerializedMeterModel;
  /** a meter module (meter_addon): its Drive folder has docs/ and photos/ like a model's */
  module?: SerializedMeterAddon;
  folderType: DriveFolderType;
  emptyMessage: string;
  source: MeterModelFilesCardType;
  meter?: PopulatedCombinedMeter | null;
  pageSize?: number;
  enablePreview?: boolean;
  visible?: boolean;
}

export const ModelLibraryFilesGallery: FC<ModelLibraryFilesGalleryProps> = ({
  model,
  module,
  folderType,
  emptyMessage,
  source,
  meter = null,
  pageSize = 20,
  enablePreview = false,
  visible = true,
}) => {
  const theme = useTheme();
  const [docsPage, setDocsPage] = useState(1);
  const { meterModelsApiClient, meterModulesApiClient } = useServices();
  const {
    Modal: DocPreviewModal,
    isOpen: isOpenDocPreviewModal,
    url,
    fileName,
    isLoading: isLoadingDocPreviewModal,
    handleOpenDocFile,
    closeModal: closeDocPreviewModal,
  } = useDocPreviewModal(
    source === MeterModelFilesCardType.Module
      ? module && (driveFileId => meterModulesApiClient.downloadFile({ moduleId: module.id, driveFileId }))
      : model && (driveFileId => meterModelsApiClient.downloadFile({ modelId: model.id, driveFileId })),
  );

  const queryParams = useMemo(
    () => ({
      modelId: model?.id,
      folderType,
      pageSize,
      resize: true as const,
    }),
    [model?.id, folderType, pageSize],
  );

  const shouldFetchModelFiles =
    source === MeterModelFilesCardType.Model && visible && !!model?.id && !!model?.folder_drive_id;

  const shouldFetchMeterFiles = source === MeterModelFilesCardType.Meter && visible && !!meter?._id;

  const shouldFetchModuleFiles =
    source === MeterModelFilesCardType.Module && visible && !!module?.id && !!module?.folder_drive_id;

  const {
    data: files,
    isLoading: isLoadingModelFiles,
    isError: isModelError,
  } = useMeterModelFiles(queryParams, { enabled: shouldFetchModelFiles });

  const {
    data: meterFiles,
    isLoading: isLoadingMeterFiles,
    isError: isMeterError,
  } = useFiles({ collections: [Collection.Meters], meters: [meter?._id] }, { enabled: shouldFetchMeterFiles });

  const {
    data: moduleFiles,
    isLoading: isLoadingModuleFiles,
    isError: isModuleError,
  } = useMeterModuleFiles(
    { moduleId: module?.id ?? 0, folderType, pageSize, resize: true },
    { enabled: shouldFetchModuleFiles },
  );

  const isLoading = isLoadingModelFiles || isLoadingMeterFiles || isLoadingModuleFiles;

  // model and module files come in the same shape (Drive metadata + resized image buffer)
  const modelFileItems = useMemo(() => {
    const driveFiles = source === MeterModelFilesCardType.Module ? moduleFiles : files;
    if (!driveFiles) return [];

    return driveFiles.map(({ metadata: { id, name, mimeType }, buffer }) => {
      const isImage = mimeType?.startsWith('image/');
      const dataUrl = buffer ? getImageSrcFromBuffer(buffer) : undefined;
      return {
        id,
        name,
        mimeType,
        imageSrc: isImage ? dataUrl : undefined,
        driveFileId: !isImage ? id : undefined,
        isImage,
      };
    });
  }, [source, files, moduleFiles]);

  const meterFileItems = useMemo(() => {
    if (!meterFiles?.result) return [];

    const photoFileValues = [MeterFileValue.Context, MeterFileValue.Detail];

    return meterFiles.result
      .filter(file => file.type === FileType.Meter && photoFileValues.includes(file.value))
      .map(({ _id, name, signedUrl, ext, value }) => ({
        id: _id,
        name,
        mimeType: ext,
        imageSrc: signedUrl,
        driveFileId: undefined,
        isImage: true,
        value,
      }));
  }, [meterFiles]);

  if (!visible) return null;

  if (isLoading) {
    return (
      <Flex align="center" justify="center" orientation="vertical" gap={16}>
        <Spin size="large" />
        <Text type="secondary">Loading {folderType}...</Text>
      </Flex>
    );
  }

  const allItems = (() => {
    if (!source) return [...modelFileItems, ...meterFileItems];

    if (source === MeterModelFilesCardType.Meter) return meterFileItems;
    return modelFileItems;
  })();

  const hasError = { model: isModelError, module: isModuleError, meter: isMeterError }[source];
  const isEmptyFolder = hasError || allItems.length === 0;
  const photos = allItems.filter(file => file.isImage && file.imageSrc);
  const documents = allItems.filter(file => !(file.isImage && file.imageSrc));
  // a folder with fewer pages (another model or module shown) never leaves an empty page
  const shownDocsPage = Math.min(docsPage, Math.max(1, Math.ceil(documents.length / DOCS_PER_PAGE)));

  return (
    <>
      <Flex orientation="vertical" gap={16} style={{ height: '100%' }}>
        {isEmptyFolder ? (
          <Flex align="center" justify="center" orientation="vertical" gap={16} style={{ flex: 1 }}>
            <Icon
              $fontSize={`${theme.font.size['4xl']}px`}
              color={hasError ? theme.color.status.error : theme.color.type.secondary}
              iconString={hasError ? 'triangleExclamation' : 'folderOpen'}
            />
            <Text type={hasError ? 'danger' : 'secondary'}>
              {hasError
                ? `Couldn't load ${DRIVE_FOLDER_TYPE_TRANSLATED[folderType].toLowerCase()}. Try again later.`
                : emptyMessage}
            </Text>
          </Flex>
        ) : (
          <>
            {photos.length > 0 && (
              <Image.PreviewGroup preview={enablePreview}>
                {/* not infinite: the slider clones slides for that, and the preview would list every photo twice */}
                <StyledCarousel arrows={photos.length > 1} dots={photos.length > 1} infinite={false}>
                  {photos.map(file => (
                    <Image
                      key={file.id}
                      alt={file.name}
                      height={PHOTO_HEIGHT}
                      src={file.imageSrc}
                      styles={{ image: { objectFit: 'contain' } }} // antd 6: `style` reaches the wrapper, not the <img>
                      width="100%"
                      preview={enablePreview ? { mask: 'Preview', src: file.imageSrc } : false}
                    />
                  ))}
                </StyledCarousel>
              </Image.PreviewGroup>
            )}
            {documents.length > 0 && (
              // pages of DOCS_PER_PAGE tiles in a 2 × 2 grid: the card keeps its height however many documents
              <Flex align="center" gap={8} orientation="vertical">
                <StyledDocumentGrid>
                  {documents.slice((shownDocsPage - 1) * DOCS_PER_PAGE, shownDocsPage * DOCS_PER_PAGE).map(file => (
                    <FileCard
                      key={file.id}
                      fileId={file.id}
                      fileName={file.name}
                      onOpen={file.driveFileId ? handleOpenDocFile(file.driveFileId, file.name) : undefined}
                    />
                  ))}
                </StyledDocumentGrid>
                {documents.length > DOCS_PER_PAGE && (
                  <Pagination
                    simple
                    current={shownDocsPage}
                    pageSize={DOCS_PER_PAGE}
                    size="small"
                    total={documents.length}
                    onChange={setDocsPage}
                  />
                )}
              </Flex>
            )}
          </>
        )}
      </Flex>

      <DocPreviewModal
        isOpen={isOpenDocPreviewModal}
        url={url}
        fileName={fileName}
        isLoading={isLoadingDocPreviewModal}
        onClose={closeDocPreviewModal}
      />
    </>
  );
};
